import React, { useState } from 'react';
import { useLocation } from '../context/LocationContext';
import './DateTimeSelectionModal.css';

const getLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DateTimeSelectionModal = ({ isOpen, onClose, onConfirm }) => {
  const { selectedLocation } = useLocation();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [address, setAddress] = useState({
    streetAddress: '',
    landmark: '',
    floor: '',
    flatNumber: ''
  });

  React.useEffect(() => {
    const intervalId = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(intervalId);
  }, []);

  // Generate time slots from 6 AM to 8 PM (in 1-hour intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 20; hour++) {
      const time12 = hour > 12 ? `${hour - 12}:00 PM` : hour === 12 ? '12:00 PM' : `${hour}:00 AM`;
      const time24 = `${hour.toString().padStart(2, '0')}:00`;
      slots.push({ label: time12, value: time24 });
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const getMinDate = () => {
    return getLocalDate(currentTime);
  };

  const isTimeSlotUnavailable = (slot, date = selectedDate, now = currentTime) => {
    if (date !== getLocalDate(now)) {
      return false;
    }

    return new Date(`${date}T${slot.value}:00`) <= now;
  };

  const selectedSlot = timeSlots.find(slot => slot.value === selectedTimeSlot);
  const selectedTimeIsUnavailable = selectedSlot && isTimeSlotUnavailable(selectedSlot);

  const handleConfirm = () => {
    if (!selectedDate || !selectedTimeSlot) {
      alert('Please select both date and time slot');
      return;
    }

    if (!address.streetAddress.trim()) {
      alert('Please enter the street address');
      return;
    }

    if (!selectedLocation) {
      alert('Please select a location first');
      return;
    }

    const now = new Date();
    if (!selectedSlot || isTimeSlotUnavailable(selectedSlot, selectedDate, now)) {
      setCurrentTime(now);
      setSelectedTimeSlot('');
      alert('Please select a time slot later than the current time');
      return;
    }
    
    onConfirm({
      date: selectedDate,
      timeSlot: selectedTimeSlot,
      timeLabel: selectedSlot.label,
      address: {
        ...address,
        city: selectedLocation.cityName,
        apartment: selectedLocation.apartmentName,
        pin: selectedLocation.pin
      }
    });
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="datetime-modal-overlay" onClick={onClose}>
      <div className="datetime-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="datetime-modal-header">
          <h2>Select Date & Time</h2>
          <button className="datetime-modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="datetime-modal-body">
          {selectedLocation && (
            <div className="location-display-section">
              <label className="datetime-label">
                <span className="label-icon">📍</span>
                Service Location
              </label>
              <div className="location-display">
                <div className="location-display-item">
                  <strong>City:</strong> {selectedLocation.cityName}
                </div>
                <div className="location-display-item">
                  <strong>Apartment:</strong> {selectedLocation.apartmentName}
                </div>
              </div>
            </div>
          )}

          <div className="datetime-section">
            <label className="datetime-label">
              <span className="label-icon">🏠</span>
              Complete Address
            </label>
            <div className="address-fields">
              <div className="address-field">
                <label htmlFor="streetAddress">Street Address / Building Name *</label>
                <input
                  type="text"
                  id="streetAddress"
                  name="streetAddress"
                  className="address-input"
                  value={address.streetAddress}
                  onChange={handleAddressChange}
                  placeholder="e.g., Building Name, Street Name"
                  required
                />
              </div>
              <div className="address-row">
                <div className="address-field">
                  <label htmlFor="floor">Floor</label>
                  <input
                    type="text"
                    id="floor"
                    name="floor"
                    className="address-input"
                    value={address.floor}
                    onChange={handleAddressChange}
                    placeholder="e.g., 3rd Floor"
                  />
                </div>
                <div className="address-field">
                  <label htmlFor="flatNumber">Flat / Unit Number</label>
                  <input
                    type="text"
                    id="flatNumber"
                    name="flatNumber"
                    className="address-input"
                    value={address.flatNumber}
                    onChange={handleAddressChange}
                    placeholder="e.g., A-101"
                  />
                </div>
              </div>
              <div className="address-field">
                <label htmlFor="landmark">Landmark (Optional)</label>
                <input
                  type="text"
                  id="landmark"
                  name="landmark"
                  className="address-input"
                  value={address.landmark}
                  onChange={handleAddressChange}
                  placeholder="e.g., Near Metro Station"
                />
              </div>
            </div>
          </div>

          <div className="datetime-section">
            <label className="datetime-label">
              <span className="label-icon">📅</span>
              Select Date
            </label>
            <input
              type="date"
              className="date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={getMinDate()}
              required
            />
            {selectedDate && (
              <p className="selected-date-text">
                Selected: {new Date(selectedDate).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
          </div>

          <div className="datetime-section">
            <label className="datetime-label">
              <span className="label-icon">🕐</span>
              Select Time Slot
            </label>
            <div className="time-slots-grid">
              {timeSlots.map((slot) => (
                <button
                  key={slot.value}
                  type="button"
                  className={`time-slot-btn ${selectedTimeSlot === slot.value ? 'selected' : ''}`}
                  disabled={isTimeSlotUnavailable(slot)}
                  onClick={() => setSelectedTimeSlot(slot.value)}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="datetime-modal-footer">
          <button className="datetime-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="datetime-confirm-btn" 
            onClick={handleConfirm}
            disabled={!selectedDate || !selectedTimeSlot || selectedTimeIsUnavailable || !address.streetAddress.trim()}
          >
            Continue to Payment
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateTimeSelectionModal;

